/**
 * Horographia — 시계 용어 정리 (교육용, 역할·읽는 법·사례 포함).
 */

export type GlossaryTerm = {
  id: string;
  termKo: string;
  termEn: string;
  category: 'case' | 'dial' | 'movement' | 'function' | 'finish' | 'misc';
  body: string;
};

export const GLOSSARY_CATEGORIES: { id: GlossaryTerm['category']; labelKo: string }[] = [
  { id: 'case', labelKo: '케이스·외장' },
  { id: 'dial', labelKo: '다이얼·표시' },
  { id: 'movement', labelKo: '무브먼트' },
  { id: 'function', labelKo: '기능·컴플리케이션' },
  { id: 'finish', labelKo: '마감·재료' },
  { id: 'misc', labelKo: '기타' },
];

export const glossary: GlossaryTerm[] = [
  // —— case ——

  {
    id: 'bezel',
    termKo: '베젤',
    termEn: 'Bezel',
    category: 'case',
    body: '크리스탈을 감싸는 케이스 바깥 테두리. 장식용 고정 베젤과, 다이빙용처럼 돌려 경과 시간을 재는 회전 베젤로 나뉜다. 데이토나·스피드마스터는 타키미터 눈금이 새겨진 고정 베젤이 상징이고, 서브마리너·시마스터 다이버는 단방향 회전이 흔하다. 보는 각도에 따라 케이스 실루엣의 인상이 크게 바뀐다.',
  },
  {
    id: 'crown',
    termKo: '크라운',
    termEn: 'Crown',
    category: 'case',
    body: '태엽을 감거나 시·분을 맞추고, 날짜·GMT 등을 조작하는 용두. 그냥 뽑는 타입과, 돌려 잠가 방수를 강화하는 스크류 다운이 있다. IWC 파일럿의 양파형처럼 장갑 조작을 전제로 크게 만든 디자인도 있다. 위치는 보통 3시이며, 왼손잡이·디자인 변형은 9시 등에도 둔다.',
  },
  {
    id: 'pusher',
    termKo: '푸셔',
    termEn: 'Pusher',
    category: 'case',
    body: '크로노그래프를 시작·정지·리셋하는 버튼. 보통 2시와 4시 양쪽에 두고, 방수 스포츠 모델은 스크류 다운 푸셔로 잠그기도 한다. 푸셔 감도와 컬럼 휠·클러치 구조는 조작감과 내구성에 영향을 준다. 알람·슬라이드 룰러 등 다른 기능 버튼도 넓게는 푸셔로 부른다.',
  },
  {
    id: 'lug',
    termKo: '러그',
    termEn: 'Lug',
    category: 'case',
    body: '스트랩이나 브레이슬릿이 걸리는 케이스의 뿔 부분. 러그 안쪽 폭(mm)이 밴드 호환의 기준이고, 러그 투 러그 길이는 손목에 맞는 착용감을 가늠한다. 각도와 두께에 따라 같은 케이스 직경이라도 커 보이거나 작아 보인다. 통합 브레이슬릿 모델은 러그가 케이스와 한 덩어리처럼 이어진다.',
  },
  {
    id: 'crystal',
    termKo: '크리스탈',
    termEn: 'Crystal',
    category: 'case',
    body: '다이얼을 덮는 투명창. 사파이어는 긁힘에 강하고 오늘날 고급·중급의 기본이며, 미네랄은 중간, 헤살라이트(아크릴)는 스피드마스터 프로 전통 사양처럼 충격에 잘 깨지지 않고 광택 복원이 쉽다. 반사 방지(AR) 코팅, 돔·플랫 형태, 사이클로프스 렌즈 부착 여부도 함께 본다.',
  },
  {
    id: 'caseback',
    termKo: '케이스백',
    termEn: 'Case back',
    category: 'case',
    body: '케이스 뒷면. 스크류 솔리드 백은 방수·견고함과 손목 쪽 마모 보호에 유리하고, 전시백(사파이어 창)은 로터·브리지·마감을 보여 준다. 스냅 백은 엔트리·쿼츠에 많고, 사파이어 전시와 솔리드를 교체 가능한 모델도 있다. 각인·시리얼·방수 표기는 대개 여기 또는 미드케이스에 있다.',
  },
  {
    id: 'oyster',
    termKo: '오이스터 케이스',
    termEn: 'Oyster case',
    category: 'case',
    body: '롤렉스가 확립한 방수 케이스 전통의 이름. 스크류 다운 케이스백·스크류 다운 크라운·견고한 미드케이스를 묶여 이야기한다. 오이스터 브레이슬릿은 3열 링크의 대표 메탈 밴드다. 「오이스터」가 붙어도 세대·모델마다 방수 수치와 구조 디테일은 다르다.',
  },
  {
    id: 'crown-guards',
    termKo: '크라운 가드',
    termEn: 'Crown guards',
    category: 'case',
    body: '용두 양옆에 튀 나온 보호 돌기. 충격·마모로부터 크라운을 감싸고, 스포츠 롤렉스·일부 다이버에서 흔히 본다. 가드 때문에 크라운 조작 각도가 좁아지기도 해서, 디자인과 실용성의 타협점이다. 없는 모델은 실루엣이 더 매끈하다.',
  },
  {
    id: 'bracelet',
    termKo: '브레이슬릿',
    termEn: 'Bracelet',
    category: 'case',
    body: '금속 링크 밴드. 오이스터(3열)·주빌리(5열)·프레지던트 등 링크 구성과, 로열 오크·노틸러스형처럼 케이스와 이어지는 통합형이 대표적이다. 브러시·폴리시 교차 마감이 고급 스포츠의 인상을 좌우한다. 엔드링크·클라스프 미세 조절은 착용감의 핵심이다.',
  },
  {
    id: 'clasp',
    termKo: '버클·클라스프',
    termEn: 'Clasp / Buckle',
    category: 'case',
    body: '스트랩·브레이슬릿을 잠그는 장치. 핀 버클은 클래식, 디플로이언트(접이식)는 메탈·가죽 모두에 쓰이며 풀림 방지에 유리하다. 미세 조절·익스텐션(다이빙·여름철) 기능이 있으면 공구 없이 길이를 맞출 수 있다. 브랜드 엠블럼이 클라스프에 새겨지는 경우가 많다.',
  },
  {
    id: 'gasket',
    termKo: '가스켓',
    termEn: 'Gasket',
    category: 'case',
    body: '크라운·케이스백·크리스탈 주변의 고무·합성 밀폐 링. 물이 스며들지 않게 하는 방수의 핵심이며, 시간이 지나면 硬化·균열되어 교체가 필요하다. 서비스 때 가스켓 상태를 점검하는 이유가 여기 있다. 방수 수치는 가스켓·케이스·크라운이 함께 맞을 때의 설계값이다.',
  },
  {
    id: 'helium-valve',
    termKo: '헬륨 밸브',
    termEn: 'Helium escape valve',
    category: 'case',
    body: '포화 잠수 후 케이스 안에 스며든 헬륨이 감압 때 팽창하며 크리스탈을 밀지 않도록, 가스를 밖으로 빼 주는 밸브. 시마스터 플래닛 오션 등 프로 다이버에 있다. 일반 수영·스노클에는 거의 필요 없고, 「프로 다이버」 포지셔닝의 상징으로도 읽힌다.',
  },
  {
    id: 'midcase',
    termKo: '미드케이스',
    termEn: 'Mid-case',
    category: 'case',
    body: '베젤과 케이스백 사이의 본체. 두께·실루엣·러그 각도·크라운 위치가 여기서 결정된다. 모노블록으로 한 덩어리 깎기도 하고, 여러 파트를 조립하기도 한다. 옆모습 사진에서 브랜드 특성이 가장 잘 드러나는 부분이다.',
  },
  {
    id: 'screw-down-crown',
    termKo: '스크류 다운 크라운',
    termEn: 'Screw-down crown',
    category: 'case',
    body: '케이스에 돌려 잠그는 용두. 잠긴 상태에서 방수 성능을 높이기 위해 스포츠·다이버에 널리 쓰인다. 시각을 맞출 때는 먼저 풀어 뽑아야 해서, 일상 조작은 일반 크라운보다 한 단계 많다. 풀린 채 물에 들어가면 방수가 크게 떨어진다.',
  },
  {
    id: 'onion-crown',
    termKo: '양파형 크라운',
    termEn: 'Onion crown',
    category: 'case',
    body: '크게 부풀어 세로 홈이 깊은 용두. 장갑을 낀 채 돌리기 쉽게 만든 파일럿·관찰시계의 상징이다. IWC 빅 파일럿 등에서 비율이 과장되어 보이며, 디자인 아이덴티티의 일부가 된다. 크라운 가드와 함께 쓰면 조작 공간이 더 중요해진다.',
  },
  {
    id: 'integrated-bracelet',
    termKo: '통합 브레이슬릿',
    termEn: 'Integrated bracelet',
    category: 'case',
    body: '케이스와 한 덩어리처럼 이어지는 메탈 밴드. 로열 오크·노틸러스·오버시즈 등 1970년대 이후 스포츠 럭셔리의 전형이다. 일반 스프링바 스트랩보다 엔드링크·테이퍼 설계가 케이스 전용인 경우가 많아, 호환 밴드가 제한된다.',
  },
  {
    id: 'fluted-bezel',
    termKo: '플루티드 베젤',
    termEn: 'Fluted bezel',
    category: 'case',
    body: '세로 홈이 반복된 골든(또는 바이컬러) 베젤. 롤렉스 데이트저스트 등에서 상징적으로 쓰이며, 빛에 따라 반짝이는 장식 효과와 브랜드 시그니처를 동시에 준다. 강철 플루티드도 있으나 「골드 플루티드」이미지가 강하다.',
  },
  {
    id: 'sapphire-cabochon',
    termKo: '사파이어 카보숑',
    termEn: 'Sapphire cabochon',
    category: 'case',
    body: '용두 끝에 올린 둥근 사파이어(또는 유사 보석). 까르띠에 탱크·산토스에서 특히 눈에 띄는 주얼리 하우스 디테일이다. 기능보다는 장식·브랜드 시그니처에 가깝다.',
  },
  {
    id: 'spring-bars',
    termKo: '스프링바',
    termEn: 'Spring bars',
    category: 'case',
    body: '러그와 스트랩을 잇는 스프링 핀. 양끝 핀이 러그 구멍에 걸려 밴드를 고정한다. 퀵릴리즈 타입은 공구 없이 밴드를 바꿀 수 있어 스트랩 교체가 쉽다. 곡선형·직선형, 두께 규격이 맞지 않으면 빠지거나 유격이 생긴다.',
  },
  {
    id: 'lug-to-lug',
    termKo: '러그 투 러그',
    termEn: 'Lug-to-lug',
    category: 'case',
    body: '한쪽 러그 끝에서 반대쪽 끝까지의 직선 길이. 케이스 직경보다 실제 손목 점유를 더 잘 보여 준다. 짧은 러그 투 러그는 작은 손목에도 안정적이고, 길면 커 보이거나 넘어갈 수 있다. 스펙 표에서 착용감을 가늠할 때 먼저 볼 수치다.',
  },
  {
    id: 'case-thickness',
    termKo: '케이스 두께',
    termEn: 'Case thickness',
    category: 'case',
    body: '크리스탈 위에서 케이스백까지 잰 높이. 드레스는 얇을수록 소매에 잘 들어가고, 다이버·파일럿·크로노는 방수·무브먼트·크리스탈 때문에 두꺼운 편이 많다. 전시백·돔 크리스탈은 체감 두께를 더한다. 「슬림」마케팅과 실제 mm를 함께 보는 편이 좋다.',
  },
  {
    id: 'exhibition-back',
    termKo: '전시백',
    termEn: 'Exhibition case back',
    category: 'case',
    body: '사파이어 창으로 무브먼트·로터·마감을 보여 주는 케이스백. 스크류 솔리드 백의 견고함·방수 이미지와 대비된다. 하이엔드·매뉴팩처 마케팅에서 자주 쓰이며, 손목 쪽 스크래치에 더 민감할 수 있다.',
  },
  {
    id: 'soft-iron-cage',
    termKo: '연철 이너 케이스',
    termEn: 'Soft-iron inner case',
    category: 'case',
    body: '자기장으로부터 무브먼트를 가리는 연철 내부 실드. IWC 파일럿 등 항자성(anti-magnetic) 설계에 쓰인다. 현대에는 실리콘 헤어스프링 등으로 자기 저항을 높이기도 해서, 연철 케이지가 유일한 해법은 아니다.',
  },
  {
    id: 'monobloc',
    termKo: '모노블록 케이스',
    termEn: 'Monobloc case',
    category: 'case',
    body: '미드케이스를 한 덩어리로 깎아 만든 구조. 이음새가 적어 강성과 방수에 유리하다고 보며, 가공 난이도와 비용은 올라간다. 스포츠·프로 다이버 케이스 설명에 자주 등장한다.',
  },

  // —— dial ——

  {
    id: 'index',
    termKo: '인덱스',
    termEn: 'Index / Indices',
    category: 'dial',
    body: '시간을 읽기 위한 눈금·바·아라비아·로마 숫자 표식. 파일럿은 큰 아라비아와 12시 삼각형, 드레스는 바톤·로마가 흔하다. 인쇄인지 아플리케(부착)인지에 따라 입체감이 달라진다. 인덱스와 핸즈의 대비가 가독성의 핵심이다.',
  },
  {
    id: 'lume',
    termKo: '루미 / 야광',
    termEn: 'Lume',
    category: 'dial',
    body: '침·인덱스에 바르는 축광(야광) 도료. 빛을 머금었다가 어두운 곳에서 빛나는 Super-LumiNova 등이 흔히 언급된다. 구세대 라듐·트리튬과는 규제가 다르며, 현대는 대부분 비방사성 축광이다. 다이버·필드·파일럿에서 야간 가독성의 기준이 된다.',
  },
  {
    id: 'subdial',
    termKo: '서브다이얼',
    termEn: 'Sub-dial / Register',
    category: 'dial',
    body: '메인 다이얼 안의 작은 계측 창(레지스터). 크로노그래프의 경과 분·시·러닝 세컨즈, 파워 리저브, 스몰 세컨즈 등에 쓰인다. 배치(3·6·9시 등)가 모델 실루엣을 좌우한다. 「다이얼이 복잡하다」는 인상은 대개 서브다이얼 수와 스케일에서 온다.',
  },
  {
    id: 'chapter-ring',
    termKo: '챕터 링',
    termEn: 'Chapter ring',
    category: 'dial',
    body: '분·초 눈금이 인쇄·부착된 바깥 고리. 다이얼 본판과 베젤 사이에 두어 가독성을 높인다. 레일로드 트랙과 함께 쓰이기도 하고, 기울인 챕터 링으로 입체감을 주기도 한다.',
  },
  {
    id: 'rehaut',
    termKo: '르오 / 이너 베젤',
    termEn: 'Rehaut',
    category: 'dial',
    body: '다이얼과 크리스탈 사이의 안쪽 링(이너 베젤에 가깝게 부르기도 함). 시리얼·분 눈금·브랜드 각인이 들어가기도 한다. 롤렉스 현행에서 특히 자주 언급되며, 빛 반사와 「깊이감」에 영향을 준다.',
  },
  {
    id: 'guilloche',
    termKo: '기로셰',
    termEn: 'Guilloché',
    category: 'dial',
    body: '기계 또는 수작업으로 금속판에 새긴 반복 기하 무늬. 클래식·하이엔드 다이얼 장식의 대표이며, 빛에 따라 결이 살아 움직인다. 엔진 터닝 선반으로 하는 전통 기법과, 프레스·CNC로 비슷하게 내는 현대 공정을 구분해서 이야기하기도 한다.',
  },
  {
    id: 'tapisserie',
    termKo: '탭isserie',
    termEn: 'Tapisserie',
    category: 'dial',
    body: '오데마 피게 로열 오크의 격자형 엠보스 다이얼 패턴. 작은 사각형이 바둑판처럼 이어져 「탭isserie(직물)」로 불린다. 세대마다 사각형 크기·깊이가 달라 수집가들이 세부를 본다. 스포츠 럭셔리 다이얼 아이덴티티의 핵심이다.',
  },
  {
    id: 'applique',
    termKo: '아플리케 인덱스',
    termEn: 'Applied indices',
    category: 'dial',
    body: '인쇄가 아니라 금속 조각을 다이얼에 부착한 시 표식. 입체감과 「고급」인상을 주고, 야광을 올린 아플리케는 다이버·스포츠에 흔하다. 부착 정밀도와 마감이 단가를 가른다.',
  },
  {
    id: 'hands',
    termKo: '핸즈(침)',
    termEn: 'Hands',
    category: 'dial',
    body: '시·분·초를 가리키는 바늘. 리프·메르세데스·스워드·도핀·다트 등 형태 이름이 있다. 루미 면적, 카운터밸런스, 다층 다이얼과의 높이 간격이 가독·고급감을 좌우한다. 브랜드마다 「시그니처 핸즈」가 있다.',
  },
  {
    id: 'date-window',
    termKo: '데이트 창',
    termEn: 'Date window / aperture',
    category: 'dial',
    body: '날짜 디스크가 보이는 작은 창. 3시·6시·4시 반 등에 두며, 사이클로프스로 확대하기도 한다. 빠른 날짜 수정·월말 31일 맞춤 방식은 칼리버마다 다르다. 창의 테두리 마감도 다이얼 완성도를 보여 준다.',
  },
  {
    id: 'cyclops',
    termKo: '사이클로프스',
    termEn: 'Cyclops lens',
    category: 'dial',
    body: '날짜 창 위를 확대하는 볼록 렌즈. 롤렉스 데이트저스트 등에서 유명하며, 크리스탈에 붙이거나 일체로 가공한다. 확대 배율과 왜곡·지문 가시성은 취향이 갈린다. 「롤렉스 얼굴」의 일부로 읽히는 디테일이다.',
  },
  {
    id: 'railroad-track',
    termKo: '레일로드 트랙',
    termEn: 'Railroad minute track',
    category: 'dial',
    body: '바깥쪽 분 눈금을 기차 레일처럼 이중선으로 그린 트랙. 클래식·파일럿·빈티지풍 다이얼에 흔하다. 챕터 링과 겹쳐 쓰며 정밀한 분 읽기를 돕는다.',
  },
  {
    id: 'flinque',
    termKo: '플랭케',
    termEn: 'Flinqué',
    category: 'dial',
    body: '기로셰 위에 에나멜이나 래커를 올려 무늬가 은은하게 비치게 한 다이얼. 장식 난이도가 높아 하이엔드·아틀리에 라인에서 자주 본다. 「기로셰만」과 「플랭케」를 구분해서 설명하는 편이 정확하다.',
  },
  {
    id: 'mercedes-hands',
    termKo: '메르세데스 핸즈',
    termEn: 'Mercedes hands',
    category: 'dial',
    body: '시침 끝이 세 갈래(삼지창)로 갈라진 침. 롤렉스 스포츠(서브·GMT 등)의 대표 실루엣이다, 루미를 넓게 담기 좋다. 「메르세데스」는 별칭이며 자동차 브랜드와의 공식 관계는 없다.',
  },
  {
    id: 'dauphine-hands',
    termKo: '도핀 핸즈',
    termEn: 'Dauphine hands',
    category: 'dial',
    body: '넓고 면이 깎인 마름모꼴 침. 클래식·드레스 다이얼에 자주 쓰이며, 폴리시된 면이 빛을 받아 고급스러워 보인다. 가독은 스포츠 침보다 부드럽고, 포멀한 인상을 준다.',
  },
  {
    id: 'sword-hands',
    termKo: '스워드 핸즈',
    termEn: 'Sword hands',
    category: 'dial',
    body: '칼날처럼 곧게 뻗은 침. 까르띠에·일부 드레스·스포츠에서 본다. 끝이 뾰족해 분 눈금과의 정렬이 또렷하고, 실루엣이 단정하다.',
  },
  {
    id: 'baton-indices',
    termKo: '바톤 인덱스',
    termEn: 'Baton indices',
    category: 'dial',
    body: '막대(바) 형태의 시 표식. 아플리케로 붙이거나 인쇄한다. 미니멀 모던·스포츠 다이얼에 흔하고, 길이·두께로 브랜드 비율 감각이 드러난다.',
  },
  {
    id: 'small-seconds',
    termKo: '스몰 세컨즈',
    termEn: 'Small seconds',
    category: 'dial',
    body: '중앙 초침이 아니라 서브다이얼에 둔 초침. 클래식·수동 와인딩·옵저베이션 워치 전통에서 흔하다. 다이얼이 「비대칭·클래식」해 보이는 이유 중 하나다.',
  },
  {
    id: 'skeleton-dial',
    termKo: '스켈레톤',
    termEn: 'Skeleton dial',
    category: 'dial',
    body: '다이얼·플레이트를 뚫어 기어와 브리지를 드러낸 구조. 장식·쇼케이스 성격이 강하고, 가독은 보통 타임온리보다 떨어진다. 마감(앙글라주·펄라주 등)이 그대로 보이므로 하이엔드에서 기교를 과시할 때 쓴다.',
  },
  {
    id: 'enamel-dial',
    termKo: '에나멜 다이얼',
    termEn: 'Enamel dial',
    category: 'dial',
    body: '유리질 에나멜을 구워 올린 다이얼. 색이 깊고 오래가며, 그랑 퓨(grand feu) 등 고난도 기법은 불량률이 높아 가격이 오른다. 클래식 드레스·하이엔드에서 「진짜 에나멜」과 래커를 구분하는 것이 중요하다.',
  },
  {
    id: 'sector-dial',
    termKo: '섹터 다이얼',
    termEn: 'Sector dial',
    category: 'dial',
    body: '동심원·부채꼴 구역으로 나눈 빈티지풍 다이얼. 1930년대 계측·파일럿·관측 이미지와 연결된다. 현대 복각에서도 「섹터」스타일로 가독과 레트로 감을 동시에 노린다.',
  },
  {
    id: 'open-heart',
    termKo: '오픈하트',
    termEn: 'Open heart',
    category: 'dial',
    body: '다이얼 일부만 뚫어 밸런스 등 움직이는 부분을 보여 주는 형식. 풀 스켈레톤보다 절제된 개방이라 가독을 일부 유지하면서 「움직이는 기계」를 보여 준다.',
  },

  // —— movement ——

  {
    id: 'automatic',
    termKo: '오토매틱',
    termEn: 'Automatic',
    category: 'movement',
    body: '로터(또는 유사 장치)가 손목 움직임으로 태엽을 감는 오토매틱 무브먼트. 일상 착용만으로 동작하는 것이 장점이고, 오래 안 끼면 멈춰 크라운으로 감아야 한다. 수동만 있는 모델보다 「편의」축이고, 로터 소음·두께는 설계마다 다르다.',
  },
  {
    id: 'manual',
    termKo: '수동(핸드와인딩)',
    termEn: 'Manual wind',
    category: 'movement',
    body: '크라운으로 태엽을 감아 쓰는 수동 와인딩. 얇게 만들기 쉽고, 감는 행위 자체가 시계와의 상호작용으로 여겨진다. 스피드마스터 프로 전통 등에서 의도적으로 유지된다. 파워 리저브를 넘기면 멈춘다.',
  },
  {
    id: 'quartz',
    termKo: '쿼츠',
    termEn: 'Quartz',
    category: 'movement',
    body: '수정 진동자로 시간을 나누는 전자식 무브먼트. 정확·저렴·관리 부담이 적고, 배터리(또는 솔라)가 필요하다. 1970년대 쿼츠 위기 이후 「정확성」의 기준을 바꿨고, 하이엔드에도 하이비트·서모보상 쿼츠가 있다.',
  },
  {
    id: 'caliber',
    termKo: '칼리버',
    termEn: 'Caliber / Calibre',
    category: 'movement',
    body: '무브먼트의 형식·설계를 가리키는 이름(칼리버). 브랜드·에보슈 공급사 번호로 부르며, 크기·기능·진동수를 함께 적는다. 「인하우스 칼리버」는 자체 개발·생산 비중을 강조하는 마케팅·기술 표현이다.',
  },
  {
    id: 'coaxial',
    termKo: '코액시얼',
    termEn: 'Co-Axial',
    category: 'movement',
    body: '오메가가 채택한 코액시얼 이스케이프먼트. 마찰을 줄여 윤활 주기를 늘리려는 설계로 설명한다. 심마스터·컨스텔레이션 등 현행 라인에 넓게 쓰이며, 「오메가 기술」아이덴티티의 한 축이다.',
  },
  {
    id: 'springdrive',
    termKo: '스프링드라이브',
    termEn: 'Spring Drive',
    category: 'movement',
    body: '세이코(그랜드 세이코 등)의 스프링 드라이브. 태엽 동력을 쓰면서 전자 규제로 초침이 끊기지 않고 미끄러지듯 움직인다. 정확도와 「글라이드 모션」초침이 특징이다.',
  },
  {
    id: 'balance-wheel',
    termKo: '밸런스 휠',
    termEn: 'Balance wheel',
    category: 'movement',
    body: '탈진기와 함께 박자를 만드는 바퀴. 일정한 주기로 왕복하며 시간을 「쪼갠다」. 소재·무게 나사·프리스프링 여부가 자기·자세차·정확도에 영향을 준다.',
  },
  {
    id: 'hairspring',
    termKo: '헤어스프링',
    termEn: 'Hairspring / Balance spring',
    category: 'movement',
    body: '밸런스에 붙은 아주 가는 스프링. 수축·이완으로 주기를 안정화한다. 니바록스 등 합금과 실리콘 헤어스프링이 있으며, 후자는 자기·충격에 유리하다고 본다.',
  },
  {
    id: 'escapement',
    termKo: '이스케이프먼트',
    termEn: 'Escapement',
    category: 'movement',
    body: '태엽의 힘을 밸런스에 「한 박자씩」전달하고 되돌리는 장치. 레버(스위스) 이스케이프가 가장 흔하고, 코액시얼·디텐트 등은 변형이다. 시계의 「박동」이 여기서 들린다.',
  },
  {
    id: 'mainspring',
    termKo: '메인스프링(태엽)',
    termEn: 'Mainspring',
    category: 'movement',
    body: '동력을 저장하는 태엽. 배럴 안에 들어가며, 풀리면 시계가 멈춘다. 파워 리저브는 태엽이 유효하게 힘을 주는 시간이다. 소재·슬립 스프링 설계가 오토의 「과감김」보호와도 연결된다.',
  },
  {
    id: 'rotor',
    termKo: '로터',
    termEn: 'Rotor',
    category: 'movement',
    body: '오토매틱에서 중력·손목 움직임으로 돌아가는 반원형 추. 태엽을 감는다. 풀 로터·마이크로로터·팬들럼 등 형식이 있고, 소음·감기 효율·두께 트레이드오프가 있다.',
  },
  {
    id: 'frequency',
    termKo: '진동수(주파수)',
    termEn: 'Frequency (vph / Hz)',
    category: 'movement',
    body: '밸런스가 초당·시간당 진동하는 횟수. 2.5Hz(18,000vph)·3Hz(21,600)·4Hz(28,000)·5Hz(36,000) 등이 흔하다. 높을수록 자세차에 유리할 수 있으나 마모·에너지 소비도 고려한다.',
  },
  {
    id: 'jewels',
    termKo: '주얼(보석)',
    termEn: 'Jewels',
    category: 'movement',
    body: '피벗 마찰을 줄이려 넣는 합성 루비 등 보석 베어링. 「몇 주얼」은 기능·설계에 따른 개수이며, 많다고 무조건 우수는 아니다. 스펙 표의 기본 항목으로 남는다.',
  },
  {
    id: 'tourbillon',
    termKo: '투르비용',
    termEn: 'Tourbillon',
    category: 'movement',
    body: '밸런스·이스케이프를 회전 케이지에 넣어 중력 자세차를 평균화하려는 장치. 오늘날은 실용보다 피니시·쇼피스 성격이 강하고, 가격·두께·가독에 큰 영향을 준다.',
  },
  {
    id: 'chronometer',
    termKo: '크로노미터',
    termEn: 'Chronometer',
    category: 'movement',
    body: '공인 기관 등에서 정밀 기준을 통과한 무브먼트·시계를 가리키는 말. COSC 등이 유명하고, 브랜드 자체 인증(마스터 크로노미터 등)도 있다. 「크로노그래프」(스톱워치 기능)와 혼동하지 않는다.',
  },
  {
    id: 'regulator',
    termKo: '레귤레이터',
    termEn: 'Regulator',
    category: 'movement',
    body: '헤어스프링 유효 길이를 바꿔 진각·지각을 맞추는 장치. 또는 시·분·초 침을 분리 배치한 「레귤레이터 다이얼」형식을 가리키기도 한다. 문맥으로 구분한다.',
  },
  {
    id: 'barrel',
    termKo: '배럴',
    termEn: 'Barrel',
    category: 'movement',
    body: '태엽(메인스프링)을 담는 원통. 기어 트레인에 동력을 전달한다. 더블 배럴은 파워 리저브·토크 평탄을 노린 설계다.',
  },
  {
    id: 'hacking-seconds',
    termKo: '해킹 세컨즈',
    termEn: 'Hacking seconds',
    category: 'movement',
    body: '크라운을 뽑으면 초침이 멈춰 시각을 신호에 맞춰 동기화할 수 있는 기능. 필드·밀리터리·많은 현대 오토에 있다. 「핵 초침」이라고도 부른다.',
  },
  {
    id: 'column-wheel',
    termKo: '칼럼 휠',
    termEn: 'Column wheel',
    category: 'movement',
    body: '크로노그래프 푸셔 조작을 제어하는 톱니 기둥(컬럼). 조작감이 또렷하다고 평가받는 전통 방식이며, 캠 방식이 더 흔한 양산형이다. 하이엔드 크로노 사양에서 자주 강조된다.',
  },
  {
    id: 'vertical-clutch',
    termKo: '버티컬 클러치',
    termEn: 'Vertical clutch',
    category: 'movement',
    body: '크로노 초침을 수직으로 물려 떨림·점프를 줄이려는 클러치. 수평 클러치 대비 시작 시 침 흔들림이 적다고 본다. 현대 오토 크로노에 많다.',
  },
  {
    id: 'silicon-hairspring',
    termKo: '실리콘 헤어스프링',
    termEn: 'Silicon hairspring',
    category: 'movement',
    body: '실리콘으로 만든 헤어스프링. 자기장에 강하고 형태 안정이 좋다고 보며, 많은 매뉴팩처가 항자·안정성 마케팅에 쓴다. 전통 합금 스프링과 서비스·수리 생태계가 다를 수 있다.',
  },
  {
    id: 'in-house',
    termKo: '인하우스',
    termEn: 'In-house movement',
    category: 'movement',
    body: '브랜드가 설계·생산 비중을 크게 가져간 무브먼트라는 뜻으로 쓰는 말. 정의가 마케팅마다 달라 「완전 내재화」와 「에보슈 마감」을 구분해야 한다. 품질의 유일한 척도는 아니다.',
  },
  {
    id: 'cosc',
    termKo: 'COSC',
    termEn: 'COSC',
    category: 'movement',
    body: '스위스 공식 크로노미터 시험 기관(Contrôle Officiel Suisse des Chronomètres). 일·위치별 오차 기준을 통과하면 크로노미터로 인증한다. 완성 시계가 아닌 무브먼트 단위 시험이 기본이다.',
  },
  {
    id: 'geneva-seal',
    termKo: '제네바 실',
    termEn: 'Poinçon de Genève',
    category: 'movement',
    body: '제네바 인장(Poinçon de Genève). 제네바 지역·마감·조립 기준을 만족하는 무브먼트에 부여되는 품질 마크. 장식·수작업 기준이 까다롭다고 알려져 있다.',
  },

  // —— function ——

  {
    id: 'tachymeter',
    termKo: '타키미터',
    termEn: 'Tachymeter',
    category: 'function',
    body: '단위 시간당 속도(예: 1km 구간)를 읽는 스케일. 베젤이나 챕터에 새기며 데이토나·스피드마스터의 상징이다. 크로노로 구간을 재고 눈금이 가리키는 숫자를 읽는다. 속도 단위 가정이 눈금 설계에 들어 있다.',
  },
  {
    id: 'chronograph',
    termKo: '크로노그래프',
    termEn: 'Chronograph',
    category: 'function',
    body: '스톱워치처럼 경과 시간을 재는 기능. 푸셔로 시작·정지·리셋하고 서브다이얼·중앙 크로노 초침으로 표시한다. 「크로노미터」(정밀 인증)와 이름이 비슷해 헷갈리기 쉽다.',
  },
  {
    id: 'date',
    termKo: '데이트',
    termEn: 'Date',
    category: 'function',
    body: '날짜를 창 또는 포인터로 보여 주는 기능. 단순 데이트부터 데이데이트·연간·퍼페추얼까지 복잡도가 갈린다. 월말 수정 방식과 타임존 여행 시 조작 순서를 설명서에서 확인하는 것이 안전하다.',
  },
  {
    id: 'power-reserve',
    termKo: '파워 리저브',
    termEn: 'Power reserve',
    category: 'function',
    body: '태엽이 남아 동작하는 시간. 다이얼·케이스백 표시기로 보여 주기도 한다. 길수록 며칠 안 끼워도 버틸 수 있으나, 토크 변화·두께와의 타협이 있다.',
  },
  {
    id: 'gmt',
    termKo: 'GMT / 듀얼 타임',
    termEn: 'GMT / Dual time',
    category: 'function',
    body: '두 번째 시침(또는 베젤)으로 다른 시간대를 읽는 기능. 항공·트래블 워치의 대표이며 GMT-Master가 유명하다. 「홈 타임 / 로컬 타임」조작 방식은 칼리버마다 다르다.',
  },
  {
    id: 'perpetual-calendar',
    termKo: '퍼페추얼 캘린더',
    termEn: 'Perpetual calendar',
    category: 'function',
    body: '월 길이·윤년까지 기계적으로 맞추는 달력. 2100년 등 예외 전까지 날짜 수정을 거의 하지 않아도 된다고 설명한다. 부품·조정 난이도가 높아 하이 컴플리케이션으로 분류된다.',
  },
  {
    id: 'moonphase',
    termKo: '문페이즈',
    termEn: 'Moon phase',
    category: 'function',
    body: '달의 위상(삭·보름 등)을 디스크나 창으로 보여 주는 기능. 낭만·클래식 장식이 강하고, 정밀 문페이즈는 오차가 매우 작다.',
  },
  {
    id: 'minute-repeater',
    termKo: '미닛 리피터',
    termEn: 'Minute repeater',
    category: 'function',
    body: '버튼을 누르면 시·분을 해머와 징으로 쳐 알려 주는 장치. 제작·조정 난이도가 극히 높아 하이엔드 쇼피스다. 방수·두께·가격에 큰 제약이 따른다.',
  },
  {
    id: 'alarm',
    termKo: '알람',
    termEn: 'Alarm',
    category: 'function',
    body: '설정한 시각에 울리거나 진동하는 기능. 기계식 알람과 쿼츠·전자 알람이 있다. 크라운·푸셔로 알람 시각을 맞춘다.',
  },
  {
    id: 'flyback',
    termKo: '플라이백',
    termEn: 'Flyback chronograph',
    category: 'function',
    body: '크로노가 돌아가는 중에도 푸셔 한 번으로 리셋과 재시작을 이어 하는 기능. 항공 등에서 연속 계측에 유용하다고 설명한다.',
  },
  {
    id: 'world-time',
    termKo: '월드타임',
    termEn: 'World time',
    category: 'function',
    body: '도시 링·24시간 디스크 등으로 여러 시간대를 한눈에 보는 기능. 트래블·클래식 컴플리케이션의 한 갈래다.',
  },
  {
    id: 'annual-calendar',
    termKo: '애뉴얼 캘린더',
    termEn: 'Annual calendar',
    category: 'function',
    body: '큰달·작은달을 자동으로 맞추되, 2월만 수동 수정이 필요한 달력. 퍼페추얼보다 단순하고 데이트보다 편리하다.',
  },
  {
    id: 'diving-bezel',
    termKo: '다이빙 베젤',
    termEn: 'Unidirectional diving bezel',
    category: 'function',
    body: '다이빙 경과 시간을 재는 회전 베젤. 안전을 위해 단방향(풀리는 방향만)인 경우가 많다. 120클릭 등이 흔하고, 야광 필·세라믹 인서트가 현행 스포츠에 많다.',
  },
  {
    id: 'day-date',
    termKo: '데이데이트',
    termEn: 'Day-Date',
    category: 'function',
    body: '요일과 날짜를 함께 보여 주는 기능. 롤렉스 데이데이트처럼 창에 요일 풀네임을 넣는 형식과, 포인터 데이가 있다.',
  },
  {
    id: 'rattrapante',
    termKo: '래트라판트(스플릿 세컨즈)',
    termEn: 'Rattrapante / Split-seconds',
    category: 'function',
    body: '스플릿 세컨즈 크로노. 두 초침으로 중간 랩타임을 재고 다시 합친다. 컬럼 휠·클램프 등 구조가 복잡하다.',
  },
  {
    id: 'pulsometer',
    termKo: '펄소미터',
    termEn: 'Pulsometer',
    category: 'function',
    body: '심박 수를 눈금으로 읽는 스케일. 일정 횟수 맥을 재고 눈금이 가리키는 값을 읽는다. 메디컬·빈티지 크로노에 많다.',
  },
  {
    id: 'telemeter',
    termKo: '텔레미터',
    termEn: 'Telemeter',
    category: 'function',
    body: '번개·포성처럼 빛과 소리 시간차로 거리를 가늠하는 스케일. 군사·기상 관측 전통에서 왔다.',
  },
  {
    id: 'jumping-hours',
    termKo: '점핑 아워',
    termEn: 'Jumping hours',
    category: 'function',
    body: '시침이 연속이 아니라 창에서 숫자로 「점프」하는 표시. 디지털적 읽기와 기계 유희가 특징이다.',
  },
  {
    id: 'retrograde',
    termKo: '레트로그레이드',
    termEn: 'Retrograde',
    category: 'function',
    body: '침이 원을 다 돌지 않고 호를 왕복하며 값을 보여 주는 표시. 날짜·초 등에 쓰며 시각적으로 화려하다.',
  },
  {
    id: 'countdown-bezel',
    termKo: '카운트다운 베젤',
    termEn: 'Countdown bezel',
    category: 'function',
    body: '남은 시간을 재는 회전 베젤(요트 레이스 등). 다이빙 단방향 베젤과 눈금·회전 방향이 다를 수 있다.',
  },

  // —— finish ——

  {
    id: 'hand-finished',
    termKo: '수작업 마감',
    termEn: 'Hand-finished',
    category: 'finish',
    body: '기계 가공 뒤 사람이 다듬는 마감. 앙글라주·블랙 폴리시·표면 결 정리 등이 포함된다. 「완전 수제」와는 다르며, 비중은 브랜드·가격대마다 다르다.',
  },
  {
    id: 'brushed',
    termKo: '브러시드',
    termEn: 'Brushed',
    category: 'finish',
    body: '한 방향으로 긁어 무광·결을 낸 표면(새틴). 스포츠 케이스·브레이슬릿에 흔하고, 폴리시 면과 교차하면 입체감이 산다.',
  },
  {
    id: 'polished',
    termKo: '폴리시드',
    termEn: 'Polished',
    category: 'finish',
    body: '거울처럼 반사되게 닦은 표면. 베젤 윗면·링크 중앙 등에 쓰이며 지문·스크래치가 잘 보인다. 브러시와 대비가 고급 스포츠의 문법이다.',
  },
  {
    id: 'anglage',
    termKo: '앵글라주',
    termEn: 'Anglage / Chamfering',
    category: 'finish',
    body: '브리지·플레이트 가장자리를 45도 가까이 비스듬히 깎고 광택 내는 마감. 하이엔드 무브먼트 장식의 기본 언어다.',
  },
  {
    id: 'perlage',
    termKo: '페를라주',
    termEn: 'Perlage / Circular graining',
    category: 'finish',
    body: '원형 점 무늬를 반복해 찍은 마감(서클링). 메인 플레이트 등 잘 안 보이는 곳에도 넣어 「빈 곳」을 없앤다.',
  },
  {
    id: 'cote-de-geneve',
    termKo: '코트 드 제네브',
    termEn: 'Côtes de Genève',
    category: 'finish',
    body: '제네바 줄무늬. 브리지 위에 물결·직선 줄무늬를 넣은 장식. 로터·브리지를 전시백으로 볼 때 가장 먼저 눈에 띈다.',
  },
  {
    id: 'blued-steel',
    termKo: '블루드 스틸',
    termEn: 'Blued steel',
    category: 'finish',
    body: '열처리로 파란 산화막을 입힌 강철 침·나사. 클래식 미학과 경도의 상징으로 쓰인다. 코팅 블루와 구분해서 이야기하기도 한다.',
  },
  {
    id: 'pvd',
    termKo: 'PVD / DLC 코팅',
    termEn: 'PVD / DLC coating',
    category: 'finish',
    body: '물리 증착으로 케이스에 입히는 코팅. 블랙·그레이 등 색을 내고 내마모를 노리지만, 깊게 긁히면 속이 드러날 수 있다.',
  },
  {
    id: 'ceramic',
    termKo: '세라믹',
    termEn: 'Ceramic',
    category: 'finish',
    body: '지르코니아 등 세라믹 베젤·케이스. 긁힘에 강하고 색이 오래가며, 충격에 깨질 수 있다. 단방향 다이버 베젤 인서트에 특히 많다.',
  },
  {
    id: 'titanium',
    termKo: '티타늄',
    termEn: 'Titanium',
    category: 'finish',
    body: '가볍고 부식에 강한 금속. 스포츠·필드에 쓰이며, 강철보다 긁힘이 「희게」보이거나 느낌 다를 수 있다. 그레이 5 등 등급에 따라 가공·발색이 다르다.',
  },
  {
    id: 'gold-alloys',
    termKo: '골드 합금',
    termEn: 'Gold alloys',
    category: 'finish',
    body: '옐로·로즈·화이트 골드 등 합금. 순도(예: 18K)와 혼합 금속이 색·경도를 정한다. 케이스는 무게와 가격이 크게 오른다.',
  },
  {
    id: 'platinum',
    termKo: '플래티넘',
    termEn: 'Platinum',
    category: 'finish',
    body: '희귀·무거운 백금. 최상위 소재 포지션이며, 광택과 무게감이 특징이다. 롤렉스 플래티넘 베젤 등 포인트 사용도 있다.',
  },
  {
    id: 'satin-finish',
    termKo: '새틴 마감',
    termEn: 'Satin finish',
    category: 'finish',
    body: '브러시에 가까운 부드러운 무광 결. 새틴이라고도 하며, 지문이 덜 묻고 단정하다.',
  },
  {
    id: 'sandblasted',
    termKo: '샌드블라스트',
    termEn: 'Sandblasted',
    category: 'finish',
    body: '모래·미디어를 분사해 균일 무광으로 만든 표면. 툴워치·현대 스포츠에서 「건조한」질감을 준다.',
  },
  {
    id: 'forged-carbon',
    termKo: '포지드 카본',
    termEn: 'Forged carbon',
    category: 'finish',
    body: '탄소 섬유를 압축·성형한 복합재. 가볍고 무늬가 조각마다 다르며, 익스트림·모던 스포츠 케이스에 쓴다.',
  },
  {
    id: 'rose-gold',
    termKo: '로즈 골드',
    termEn: 'Rose / Pink gold',
    category: 'finish',
    body: '구리가 섞여 붉게 보이는 골드 합금. 피부 톤·다이얼 색과의 조화가 좋아 인기가 많다. 합금비에 따라 색이 달라진다.',
  },

  // —— misc ——

  {
    id: 'manufacture',
    termKo: '매뉴팩처',
    termEn: 'Manufacture',
    category: 'misc',
    body: '무브먼트·케이스 등을 자체 비중으로 개발·생산하는 시계 제조 형태. 「매뉴팩처」라 불러도 외주 부품 비중은 브랜드마다 다르다. 산업 라인과 아틀리에 특제의 중간~상위를 가리킬 때 쓴다.',
  },
  {
    id: 'water-resistance',
    termKo: '방수',
    termEn: 'Water resistance',
    category: 'misc',
    body: '설계상 견디는 수압의 표시(예: 100m·300m). 수심 「헤엄쳐도 되는 깊이」와 1:1이 아니며, 크라운을 잠그고 가스켓이 살아 있을 때 의미가 있다. 샤워·다이빙 가능 여부는 제조사 지침을 따른다.',
  },
  {
    id: 'msrp',
    termKo: '정가 / 시세',
    termEn: 'MSRP vs market',
    category: 'misc',
    body: '제조사 권장 소비자가(Manufacturer\'s Suggested Retail Price). 출시·카탈로그에 적힌 정가다. 실제 구매가는 AD 할인·세금·환율·그레이 마켓에 따라 달라지며, 중고·시세와는 별개다. 이 사이트 가격란도 교육용 스케일이다.',
  },
  {
    id: 'reference',
    termKo: '레퍼런스',
    termEn: 'Reference number',
    category: 'misc',
    body: '모델·사양을 구분하는 제품 번호(레퍼런스). 케이스·다이얼·베젤 조합이 바뀌면 번호가 갈린다. 수집·중고·A/S에서 「같은 이름」보다 레퍼런스가 정확한 키다.',
  },
  {
    id: 'complication',
    termKo: '컴플리케이션',
    termEn: 'Complication',
    category: 'misc',
    body: '시·분·초 이상의 부가 기능. 데이트·크로노·GMT부터 퍼페추얼·리피터까지. 많을수록 부품·조정이 복잡해지고 가격·두께가 오른다.',
  },
  {
    id: 'tool-watch',
    termKo: '툴 워치',
    termEn: 'Tool watch',
    category: 'misc',
    body: '다이빙·항공·탐험·필드처럼 실용 목적을 내세운 시계. 가독·방수·조작이 우선이고, 장식은 최소화되는 편이다. 오늘날은 패션·아이콘으로도 소비된다.',
  },
  {
    id: 'dress-watch',
    termKo: '드레스 워치',
    termEn: 'Dress watch',
    category: 'misc',
    body: '정장·포멀에 맞춘 얇고 절제된 시계. 작은 케이스·가죽 스트랩·단순 다이얼이 전형이다. 스포츠 럭셔리와 대비되는 축이다.',
  },
  {
    id: 'horology',
    termKo: '호롤로지',
    termEn: 'Horology',
    category: 'misc',
    body: '시간 측정과 시계 제작을 아우르는 분야·학예. 기술·디자인·역사·수집 문화를 함께 다룰 때 쓴다.',
  },
  {
    id: 'etat-blanc',
    termKo: '에타 블랑',
    termEn: 'Ébauche',
    category: 'misc',
    body: '다이얼·핸즈를 올리기 전 단계의 「흰 상태」케이스·무브먼트 조립. 제조 공정 설명에서 마감·품질 검수 단위로 등장한다.',
  },
  {
    id: 'service-interval',
    termKo: '오버홀 주기',
    termEn: 'Service interval',
    category: 'misc',
    body: '오일 교체·가스켓·점검 권장 주기. 흔히 수년 단위로 안내하나 사용·방수·환경에 따라 다르다. 방수 시계는 물 사용 전후 점검이 중요하다.',
  },
  {
    id: 'authorized-dealer',
    termKo: '공식 대리점(AD)',
    termEn: 'Authorized dealer (AD)',
    category: 'misc',
    body: '브랜드가 인정한 공식 판매점(AD). 보증·A/S·정상 유통 경로와 연결된다. 재고·배정·할인 정책은 점·시기마다 다르다.',
  },
  {
    id: 'grey-market',
    termKo: '그레이 마켓',
    termEn: 'Grey market',
    category: 'misc',
    body: '공식 AD 밖의 병행·그레이 유통. 가격이 낮을 수 있으나 보증·구성품·출처를 더 따져야 한다. 이 사이트는 구매 권유가 아니다.',
  },
  {
    id: 'box-papers',
    termKo: '박스 앤 페이퍼스',
    termEn: 'Box and papers',
    category: 'misc',
    body: '보증서·설명서·박스 등 구성품. 중고에서 「풀세트」여부를 가리킬 때 쓴다. 진위·보증 잔여와는 별도로 확인한다.',
  },
  {
    id: 'nos',
    termKo: 'NOS',
    termEn: 'New old stock',
    category: 'misc',
    body: 'New Old Stock. 오래전에 생산됐으나 미사용으로 남은 재고. 컨디션·가스켓·오일 상태는 「새것」과 다를 수 있다.',
  },
  {
    id: 'provenance',
    termKo: '프로비넌스',
    termEn: 'Provenance',
    category: 'misc',
    body: '소유·판매 이력·출처. 희귀·하이엔드에서 가치·진위 판단에 중요하다. 문서·서비스 기록이 근거가 된다.',
  },
  {
    id: 'beat-error',
    termKo: '비트 에러',
    termEn: 'Beat error',
    category: 'misc',
    body: '밸런스가 양쪽으로 도는 시간의 불균형. 타이밍기에서 ms로 보며, 조정 대상이다. 0에 가까울수록 이상적이나 실용 허용 범위가 있다.',
  },
  {
    id: 'amplitude',
    termKo: '앰플리튜드',
    termEn: 'Amplitude',
    category: 'misc',
    body: '밸런스가 한쪽으로 흔들리는 각도. 너무 낮거나 높으면 윤활·태엽·자세 문제를 의한다. 서비스 진단 지표다.',
  },
];

export function glossaryByCategory(cat: GlossaryTerm['category']): GlossaryTerm[] {
  return glossary.filter((t) => t.category === cat);
}
